import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  DatePicker,
  Empty,
  Form,
  Input,
  Popover,
  Space,
  Spin,
  Tooltip,
  Typography,
  theme,
  type DatePickerProps
} from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { type Dayjs } from 'dayjs';
import type { AvailableSlot, ResourcePolicy } from '@/api/mcp-appointments';
import type { PickerLocale } from 'antd/es/date-picker/generatePicker';
import { leadTimeMinutesToDays } from '@/utils/policyLeadTime';
import { appointmentSlotDatePickerLocale } from '../../slot-scheduling/appointmentSlotDatePickerLocale';
import {
  findFirstDayWithSlotsInMonth,
  getCalendarDateKey,
  getCalendarMonthKey,
  getDateKeyInTimezone,
  getMonthKeyInTimezone,
  normalizeIso,
  type DisabledTimeConfig
} from '../../slot-scheduling/utils';
import '@/assets/scss/date-time-picker.scss';
import { useCompactViewport } from '../useCompactViewport';

/**
 * Slot-backed date and time picker for appointment booking.
 *
 * Presents a read-only summary field that opens a popover with an embedded calendar and a
 * three-column grid of bookable time slots. Designed as a drop-in replacement for
 * {@link DateTimePicker} when availability comes from `useAppointmentSlotSelection`.
 */

/** Format shown in the closed trigger input after a time is chosen. */
const DISPLAY_FORMAT = 'MMM DD, YYYY [at] HH:mm';

/** Format used for individual slot buttons in the time grid. */
const TIME_FORMAT = 'HH:mm';

/**
 * Returns a short label suitable for the timezone badge in the slot panel.
 *
 * @param timezone - IANA timezone from the availability API (e.g. `Europe/Sofia`).
 * @returns Abbreviation such as `EET`, or the city name when abbreviation is unavailable.
 */
function getTimezoneShortLabel(timezone: string): string {
  try {
    const abbreviation = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'short'
    })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value;
    if (abbreviation) {
      return abbreviation;
    }
  } catch {
    // Fall through to city segment below.
  }

  const citySegment = timezone.split('/').pop();
  return citySegment?.replace(/_/g, ' ') ?? timezone;
}

/**
 * Props for the inner picker control wired to Ant Design `Form.Item` via `value` / `onChange`.
 * Typically consumed only inside {@link SlotDateTimePicker}.
 */
interface SlotDateTimePickerControlProps {
  /** Current committed start time from the form. */
  value?: Dayjs | null;
  /** Called when the user picks a slot or the selection is cleared. */
  onChange?: (value: Dayjs | null) => void;
  /** Remounts internal state when the dialog or appointment context changes. */
  dialogKey: number | string;
  /** Policy-based date guard (past days, lead time, etc.) from the slot hook. */
  disabledDate?: (current: Dayjs | null) => boolean;
  /** Month currently loaded for slot availability queries. */
  visibleMonth?: Dayjs;
  /** Fired when the user navigates to a different month in the calendar. */
  onVisibleMonthChange?: (month: Dayjs) => void;
  /** Day used to open the calendar when there is no committed start time yet. */
  preferredPickerValue?: Dayjs;
  /** Whether the form should treat `startTime` as user-confirmed (enables submit). */
  onStartTimeCommit?: (committed: boolean) => void;
  /** Fired when the user selects a calendar day; triggers first-slot autofill in the hook. */
  onCalendarDaySelect?: (date: Dayjs) => void;
  /** Placeholder on the read-only trigger input. */
  placeholder?: string;
  /** Ant Design size passed to the trigger input. */
  size?: 'large' | 'middle' | 'small';
  /** Where to mount the popover (e.g. modal body to avoid clipping). */
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  /** Available slots indexed by `YYYY-MM-DD` in the resource timezone. */
  slotsByDateKey?: Map<string, AvailableSlot[]>;
  /** IANA timezone used for day boundaries and slot labels. */
  timezone?: string;
  /** When true, shows loading state while the visible month's slots are fetched. */
  isLoadingSlots?: boolean;
  /** When true, days without slot data are not disabled (truncated API response). */
  slotsTruncated?: boolean;
  /** Ignored for the calendar; week always starts on Monday via `appointmentSlotDatePickerLocale`. */
  locale?: PickerLocale;
  /** Renders as a trigger popover or always-visible inline panel. */
  displayMode?: 'popover' | 'inline';
}

/**
 * Public props for {@link SlotDateTimePicker}. Mirrors {@link DateTimePicker} where possible
 * so callers can swap imports with minimal changes, plus slot data from the selection hook.
 */
interface SlotDateTimePickerProps {
  /** Resource policy used for the default label tooltip. */
  resourcePolicy?: ResourcePolicy | undefined;
  /** Appointment duration in minutes (kept for API parity with `DateTimePicker`). */
  duration: number;
  /** Optional form reference (kept for API parity; not used internally). */
  form?: unknown;
  /** Remounts the control when the dialog or appointment context changes. */
  dialogKey: number | string;
  /** Policy-based date guard from `useAppointmentSlotSelection`. */
  disabledDate?: (current: Dayjs | null) => boolean;
  /** Kept for API parity with `DateTimePicker`; not used by this component. */
  disabledTime?: (current: Dayjs | null) => DisabledTimeConfig;
  /** Kept for API parity with `DateTimePicker`; not used by this component. */
  slotAlignmentMinutes?: number;
  /** Kept for API parity with `DateTimePicker`; always treated as slot-backed. */
  useSlotSchedule?: boolean;
  /** Month whose slots are loaded in the parent hook. */
  visibleMonth?: Dayjs;
  /** Notifies the parent when the user changes the visible calendar month. */
  onVisibleMonthChange?: (month: Dayjs) => void;
  /** Initial calendar focus when opening without a committed time. */
  preferredPickerValue?: Dayjs;
  /** Updates `hasCommittedStartTime` in the slot selection hook. */
  onStartTimeCommit?: (committed: boolean) => void;
  /** Triggers first-slot autofill when a new day is chosen. */
  onCalendarDaySelect?: (date: Dayjs) => void;
  /** Overrides the default `Form.Item` label. */
  label?: React.ReactNode;
  /** Overrides the default policy tooltip on the label. */
  tooltip?: React.ReactNode;
  /** Overrides the trigger input placeholder. */
  placeholder?: string;
  /** Optional help text below the field. */
  help?: React.ReactNode;
  /** Ant Design size for the trigger input. */
  size?: 'large' | 'middle' | 'small';
  /** Popover mount target (forwarded to the inner control). */
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  /** Ignored for the calendar; week always starts on Monday via `appointmentSlotDatePickerLocale`. */
  locale?: PickerLocale;
  /** Slots for the visible month, keyed by date in the resource timezone. */
  slotsByDateKey?: Map<string, AvailableSlot[]>;
  /** Resource timezone from the availability API. */
  timezone?: string;
  /** Whether slots for the selected month are still loading. */
  isLoadingSlots?: boolean;
  /** Whether the slot list response was truncated. */
  slotsTruncated?: boolean;
  /** Renders as a trigger popover or always-visible inline panel. */
  displayMode?: 'popover' | 'inline';
}

/**
 * Interactive popover picker: calendar on the left, bookable time slots on the right.
 *
 * Disables days with no availability, marks days that have slots with a dot, and keeps the
 * slot panel in sync when the user changes months via the header arrows.
 */
const SlotDateTimePickerControl: React.FC<SlotDateTimePickerControlProps> = ({
  value,
  onChange,
  dialogKey,
  disabledDate,
  visibleMonth,
  onVisibleMonthChange,
  preferredPickerValue,
  onStartTimeCommit,
  onCalendarDaySelect,
  placeholder,
  size = 'middle',
  getPopupContainer,
  slotsByDateKey,
  timezone = 'UTC',
  isLoadingSlots = false,
  slotsTruncated = false,
  locale: _locale = appointmentSlotDatePickerLocale,
  displayMode = 'popover'
}) => {
  const { token } = theme.useToken();
  const { t } = useTranslation('manage');
  const [open, setOpen] = useState(false);
  const [calendarReady, setCalendarReady] = useState(false);
  const [panelMonth, setPanelMonth] = useState<Dayjs>(() => dayjs().startOf('month'));
  const [selectedDay, setSelectedDay] = useState<Dayjs | null>(null);
  const lastCalendarDayRef = useRef<string | null>(null);
  const pendingMonthRef = useRef<Dayjs | null>(null);
  const lastVisibleMonthEmitRef = useRef<string | null>(null);
  const initializedDialogKeyRef = useRef<number | string | null>(null);
  const calendarMountRef = useRef<HTMLDivElement>(null);
  const isInlineMode = displayMode === 'inline';
  const isPanelVisible = isInlineMode || open;
  // On narrow viewports the stylesheet stacks calendar above slots, so the panel must grow with its content.
  const isCompactPanel = useCompactViewport();

  useEffect(() => {
    if (!isPanelVisible) {
      setCalendarReady(false);
      return;
    }
    const frame = requestAnimationFrame(() => setCalendarReady(true));
    return () => cancelAnimationFrame(frame);
  }, [isPanelVisible]);

  const resolvedTimezone = timezone || 'UTC';

  useEffect(() => {
    if (initializedDialogKeyRef.current !== dialogKey) {
      initializedDialogKeyRef.current = dialogKey;

      const source = preferredPickerValue ?? visibleMonth ?? dayjs();
      if (source?.isValid()) {
        setPanelMonth(source.startOf('month'));
        setSelectedDay(null);
        lastCalendarDayRef.current = null;
        lastVisibleMonthEmitRef.current = null;
      }
      return;
    }

    if (preferredPickerValue?.isValid()) {
      const preferredMonth = preferredPickerValue.startOf('month');
      setPanelMonth((currentMonth) => (
        currentMonth.isSame(preferredMonth, 'month') ? currentMonth : preferredMonth
      ));
    }
  }, [dialogKey, preferredPickerValue, visibleMonth]);

  const emitVisibleMonthChange = useCallback(
    (month: Dayjs) => {
      const monthKey = getCalendarMonthKey(month);
      if (!monthKey || monthKey === lastVisibleMonthEmitRef.current) {
        return;
      }
      const controlledMonthKey = visibleMonth?.isValid()
        ? getCalendarMonthKey(visibleMonth)
        : null;
      if (controlledMonthKey === monthKey) {
        lastVisibleMonthEmitRef.current = monthKey;
        return;
      }
      lastVisibleMonthEmitRef.current = monthKey;
      onVisibleMonthChange?.(month);
    },
    [onVisibleMonthChange, visibleMonth]
  );

  useEffect(() => {
    if (visibleMonth?.isValid()) {
      const nextMonth = visibleMonth.startOf('month');
      setPanelMonth((currentMonth) => (
        currentMonth.isSame(nextMonth, 'month') ? currentMonth : nextMonth
      ));
    }
  }, [visibleMonth]);

  /** `YYYY-MM` of the calendar month currently on screen (browser-local civil month). */
  const panelMonthKey = useMemo(
    () => getCalendarMonthKey(panelMonth),
    [panelMonth]
  );

  useEffect(() => {
    if (!value?.isValid()) {
      return;
    }
    const valueMonthKey = getMonthKeyInTimezone(value, resolvedTimezone);
    if (valueMonthKey === panelMonthKey) {
      setSelectedDay(value.startOf('day'));
      lastCalendarDayRef.current = getCalendarDateKey(value.startOf('day'));
    }
  }, [value, resolvedTimezone, panelMonthKey]);

  /** Highlighted day in the embedded calendar; only set when it falls in `panelMonth`. */
  const calendarValue = useMemo(() => {
    const candidate = selectedDay ?? (value?.isValid() ? value : null);
    if (!candidate?.isValid()) {
      return undefined;
    }
    const candidateMonthKey = getCalendarMonthKey(candidate);
    if (candidateMonthKey !== panelMonthKey) {
      return undefined;
    }
    return candidate.startOf('day');
  }, [selectedDay, value, panelMonthKey]);

  /** `YYYY-MM-DD` for the day whose slot grid is shown; null when no day is selected. */
  const selectedDateKey = useMemo(() => {
    if (selectedDay?.isValid()) {
      const dayMonthKey = getCalendarMonthKey(selectedDay);
      if (dayMonthKey !== panelMonthKey) {
        return null;
      }
      return getCalendarDateKey(selectedDay);
    }

    if (value?.isValid()) {
      const valueMonthKey = getMonthKeyInTimezone(value, resolvedTimezone);
      if (valueMonthKey !== panelMonthKey) {
        return null;
      }
      return getDateKeyInTimezone(value, resolvedTimezone);
    }

    return null;
  }, [selectedDay, value, resolvedTimezone, panelMonthKey]);

  /** Raw availability records for the selected day. */
  const daySlots = useMemo(() => {
    if (!selectedDateKey || !slotsByDateKey) {
      return [];
    }
    return slotsByDateKey.get(selectedDateKey) ?? [];
  }, [selectedDateKey, slotsByDateKey]);

  /** Sorted start times for the slot button grid. */
  const slotStarts = useMemo(
    () =>
      daySlots
        .map((slot) => dayjs(slot.start))
        .filter((start) => start.isValid())
        .sort((a, b) => a.valueOf() - b.valueOf()),
    [daySlots]
  );

  /**
   * Committed time that is no longer in the fresh slot list (e.g. after reschedule).
   * Shown as an extra button so the user can keep their existing booking.
   */
  const orphanSelection = useMemo(() => {
    if (!value?.isValid() || !selectedDateKey) {
      return null;
    }
    const valueKey = getDateKeyInTimezone(value, resolvedTimezone);
    if (valueKey !== selectedDateKey) {
      return null;
    }
    const isInGrid = slotStarts.some(
      (start) => normalizeIso(start.toISOString()) === normalizeIso(value.toISOString())
    );
    return isInGrid ? null : value;
  }, [value, selectedDateKey, slotStarts, resolvedTimezone]);

  /** Text shown on the closed read-only trigger input. */
  const displayText = useMemo(() => {
    if (value && dayjs.isDayjs(value) && value.isValid()) {
      return value.format(DISPLAY_FORMAT);
    }
    return '';
  }, [value]);

  /** Updates the highlighted day and notifies the parent when the day actually changed. */
  const handleDaySelect = useCallback(
    (date: Dayjs) => {
      if (!date?.isValid()) {
        return;
      }

      const nextDay = date.startOf('day');
      if (getCalendarMonthKey(nextDay) !== panelMonthKey) {
        return;
      }
      setSelectedDay(nextDay);

      const nextDayKey = getCalendarDateKey(nextDay);
      const dayChanged = nextDayKey !== lastCalendarDayRef.current;
      lastCalendarDayRef.current = nextDayKey;

      if (dayChanged) {
        onCalendarDaySelect?.(nextDay);
      }
    },
    [onCalendarDaySelect, panelMonthKey]
  );

  /**
   * Aligns the selected day with a newly visible month: keep current day if still in month,
   * reuse the form value, pick the first day with slots, or clear when none exist.
   */
  const syncSelectionToMonth = useCallback(
    (nextMonth: Dayjs) => {
      if (!nextMonth.isValid()) {
        return;
      }

      const nextMonthKey = getCalendarMonthKey(nextMonth);
      if (!nextMonthKey) {
        return;
      }

      const currentDay = selectedDay ?? (value?.isValid() ? value.startOf('day') : null);
      if (
        currentDay?.isValid()
        && getCalendarMonthKey(currentDay) === nextMonthKey
      ) {
        if (!selectedDay) {
          setSelectedDay(currentDay.startOf('day'));
        }
        return;
      }

      if (
        value?.isValid()
        && getMonthKeyInTimezone(value, resolvedTimezone) === nextMonthKey
      ) {
        handleDaySelect(value.startOf('day'));
        return;
      }

      if (
        preferredPickerValue?.isValid()
        && getCalendarMonthKey(preferredPickerValue) === nextMonthKey
      ) {
        handleDaySelect(preferredPickerValue.startOf('day'));
        if (isLoadingSlots || !slotsByDateKey?.size) {
          pendingMonthRef.current = nextMonth;
        } else {
          pendingMonthRef.current = null;
        }
        return;
      }

      if (isLoadingSlots || !slotsByDateKey?.size) {
        pendingMonthRef.current = nextMonth;
        if (selectedDay) {
          setSelectedDay(null);
        }
        lastCalendarDayRef.current = null;
        return;
      }

      pendingMonthRef.current = null;
      const firstDayWithSlots = findFirstDayWithSlotsInMonth(
        nextMonth,
        slotsByDateKey,
        resolvedTimezone
      );
      if (firstDayWithSlots) {
        handleDaySelect(firstDayWithSlots.startOf('day'));
        return;
      }

      if (selectedDay) {
        setSelectedDay(null);
      }
      lastCalendarDayRef.current = null;
      if (value?.isValid()) {
        onChange?.(null);
        onStartTimeCommit?.(false);
      }
    },
    [
      selectedDay,
      value,
      preferredPickerValue,
      resolvedTimezone,
      isLoadingSlots,
      slotsByDateKey,
      handleDaySelect,
      onChange,
      onStartTimeCommit
    ]
  );

  /** Handles month navigation from the calendar header arrows. */
  const handlePanelChange = useCallback(
    (date: Dayjs) => {
      if (!date?.isValid()) {
        return;
      }
      const nextMonth = date.startOf('month');
      if (nextMonth.isSame(panelMonth, 'month')) {
        return;
      }
      setPanelMonth(nextMonth);
      emitVisibleMonthChange(nextMonth);
      syncSelectionToMonth(nextMonth);
    },
    [panelMonth, emitVisibleMonthChange, syncSelectionToMonth]
  );

  /** Re-runs month sync once slot data arrives for a month that was pending while loading. */
  useEffect(() => {
    const pendingMonth = pendingMonthRef.current;
    if (!pendingMonth || isLoadingSlots || !slotsByDateKey?.size) {
      return;
    }

    const pendingKey = getCalendarMonthKey(pendingMonth);
    if (pendingKey !== panelMonthKey) {
      return;
    }

    pendingMonthRef.current = null;
    syncSelectionToMonth(pendingMonth);
  }, [isLoadingSlots, slotsByDateKey, panelMonthKey, syncSelectionToMonth]);

  /** Opens or closes the popover and syncs the visible month on open. */
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) {
        pendingMonthRef.current = null;
        return;
      }

      const panelSource =
        preferredPickerValue
        ?? (value && dayjs.isDayjs(value) && value.isValid() ? value : panelMonth);
      if (panelSource?.isValid()) {
        const nextMonth = panelSource.startOf('month');
        setPanelMonth(nextMonth);
        emitVisibleMonthChange(nextMonth);
        syncSelectionToMonth(nextMonth);
      }
    },
    [preferredPickerValue, value, panelMonth, emitVisibleMonthChange, syncSelectionToMonth]
  );

  useEffect(() => {
    if (!isInlineMode) {
      return;
    }
    syncSelectionToMonth(panelMonth);
  }, [isInlineMode, panelMonth, syncSelectionToMonth]);

  /** Bridges Ant Design `DatePicker` day clicks to `handleDaySelect`. */
  const handleCalendarChange = useCallback(
    (date: Dayjs | Dayjs[] | null) => {
      if (!date) {
        return;
      }
      const nextDate = Array.isArray(date) ? date[0] : date;
      if (nextDate?.isValid()) {
        handleDaySelect(nextDate);
      }
    },
    [handleDaySelect]
  );

  /** Commits the chosen slot to the form and closes the popover. */
  const handleSlotSelect = useCallback(
    (slotStart: Dayjs) => {
      if (!slotStart.isValid()) {
        return;
      }
      onChange?.(slotStart);
      onStartTimeCommit?.(true);
      if (!isInlineMode) {
        setOpen(false);
      }
    },
    [onChange, onStartTimeCommit, isInlineMode]
  );

  /** Whether a slot button matches the current form value (ISO-normalized). */
  const isSlotSelected = useCallback(
    (slotStart: Dayjs) => {
      if (!value?.isValid()) {
        return false;
      }
      return normalizeIso(slotStart.toISOString()) === normalizeIso(value.toISOString());
    },
    [value]
  );

  const selectedDayLabel = selectedDateKey
    ? dayjs(selectedDateKey).format('ddd, MMM D')
    : null;

  const timezoneShortLabel = resolvedTimezone !== 'UTC'
    ? getTimezoneShortLabel(resolvedTimezone)
    : null;

  const slotsAriaLabel = t('appointmentComponents.create.dateTime.availableTimes');

  /** Date key of the committed form value; used to keep that day enabled when slots refresh. */
  const committedDateKey = useMemo(() => {
    if (!value?.isValid()) {
      return null;
    }
    return getDateKeyInTimezone(value, resolvedTimezone);
  }, [value, resolvedTimezone]);

  /** True when `slotsByDateKey` has at least one slot for the given calendar day. */
  const hasSlotsOnDay = useCallback(
    (current: Dayjs) => {
      const dateKey = getCalendarDateKey(current);
      if (!dateKey) {
        return false;
      }
      return (slotsByDateKey?.get(dateKey)?.length ?? 0) > 0;
    },
    [slotsByDateKey]
  );

  /** True when the day should be greyed out because it has zero slots in the loaded month. */
  const isNoSlotsDay = useCallback(
    (current: Dayjs) => {
      if (!current?.isValid() || isLoadingSlots || !slotsByDateKey?.size || slotsTruncated) {
        return false;
      }

      const monthKey = getCalendarMonthKey(current);
      if (!monthKey || !panelMonthKey || monthKey !== panelMonthKey) {
        return false;
      }

      const dateKey = getCalendarDateKey(current);
      if (!dateKey) {
        return false;
      }
      if (committedDateKey && dateKey === committedDateKey) {
        return false;
      }

      return !hasSlotsOnDay(current);
    },
    [
      isLoadingSlots,
      slotsByDateKey,
      slotsTruncated,
      panelMonthKey,
      committedDateKey,
      hasSlotsOnDay
    ]
  );

  /** Merges policy `disabledDate` with no-slot days for the embedded calendar. */
  const combinedDisabledDate = useCallback(
    (current: Dayjs | null) => {
      if (!current) {
        return false;
      }
      if (disabledDate?.(current)) {
        return true;
      }
      return isNoSlotsDay(current);
    },
    [disabledDate, isNoSlotsDay]
  );

  /** Custom day cell: day number plus a dot when the day has available slots. */
  const dateCellRender = useCallback<NonNullable<DatePickerProps['cellRender']>>(
    (current, info) => {
      // Only day cells get the availability dot; other panels (and non-date values) keep antd's default cell.
      if (info.type !== 'date' || !dayjs.isDayjs(current)) {
        return info.originNode;
      }

      const monthKey = getCalendarMonthKey(current);
      const showAvailabilityHint =
        !isLoadingSlots
        && Boolean(slotsByDateKey?.size)
        && !slotsTruncated
        && monthKey === panelMonthKey
        && hasSlotsOnDay(current);

      return (
        <div className="ant-picker-cell-inner">
          {current.date()}
          {showAvailabilityHint ? (
            <div className="slot-date-time-picker__day-dot" aria-hidden />
          ) : null}
        </div>
      );
    },
    [
      isLoadingSlots,
      slotsByDateKey,
      slotsTruncated,
      panelMonthKey,
      hasSlotsOnDay
    ]
  );

  /** Keeps the embedded `DatePicker` dropdown inside the panel mount node. */
  const resolvePopupContainer = useCallback(
    () => calendarMountRef.current ?? document.body,
    []
  );

  const pickerPanel = (
    <div
      className={`slot-date-time-picker__panel${isInlineMode ? ' slot-date-time-picker__panel--inline' : ''}`}
      style={{ height: isCompactPanel ? 'auto' : '350px' }}
    >
      <div className="slot-date-time-picker__calendar">
        <div ref={calendarMountRef} className="slot-date-time-picker__calendar-mount">
          {isLoadingSlots ? (
            <div className="slot-date-time-picker__calendar-loading" aria-live="polite">
              <Spin />
              <Typography.Text type="secondary" className="slot-date-time-picker__loading-label">
                {t('appointmentComponents.create.dateTime.loadingSlots')}
              </Typography.Text>
            </div>
          ) : null}
          {isPanelVisible && calendarReady && !isLoadingSlots ? (
            <DatePicker
              open
              picker="date"
              showToday={false}
              value={calendarValue}
              pickerValue={panelMonth}
              locale={appointmentSlotDatePickerLocale}
              disabledDate={combinedDisabledDate}
              cellRender={dateCellRender}
              onCalendarChange={handleCalendarChange}
              onPanelChange={handlePanelChange}
              getPopupContainer={resolvePopupContainer}
              onOpenChange={() => undefined}
              allowClear={false}
              inputReadOnly
              styles={{ root: { width: 0, height: 0, margin: 0, padding: 0, overflow: 'hidden', opacity: 0, position: 'absolute' } }}
            />
          ) : null}
        </div>
        <Typography.Text
          type="secondary"
          className={`slot-date-time-picker__calendar-legend${
            !isLoadingSlots && slotsByDateKey?.size && !slotsTruncated
              ? ''
              : ' slot-date-time-picker__calendar-legend--hidden'
          }`}
        >
          {t('appointmentComponents.create.dateTime.calendarLegend')}
        </Typography.Text>
      </div>
      <div
        className="slot-date-time-picker__slots"
        style={isCompactPanel ? { flex: 'none', height: 'auto', minHeight: 0, boxSizing: 'border-box' } : undefined}
      >
        <div className="slot-date-time-picker__slots-header">
          <Typography.Text
            strong
            className="slot-date-time-picker__slots-day"
            ellipsis={{ tooltip: selectedDayLabel ?? true }}
          >
            {selectedDayLabel ?? t('appointmentComponents.create.dateTime.selectDay')}
          </Typography.Text>
          {timezoneShortLabel ? (
            <Tooltip
              title={t('appointmentComponents.create.dateTime.timezoneHint', { timezone: resolvedTimezone })}
            >
              <span className="slot-date-time-picker__tz-badge">{timezoneShortLabel}</span>
            </Tooltip>
          ) : null}
        </div>
        <div className="slot-date-time-picker__slots-body">
          {isLoadingSlots ? (
            <div className="slot-date-time-picker__slots-loading" aria-live="polite">
              <Spin />
              <Typography.Text type="secondary" className="slot-date-time-picker__loading-label">
                {t('appointmentComponents.create.dateTime.loadingSlots')}
              </Typography.Text>
            </div>
          ) : !selectedDateKey ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('appointmentComponents.create.dateTime.selectDay')}
            />
          ) : slotStarts.length === 0 && !orphanSelection ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('appointmentComponents.create.dateTime.noSlotsOnDay')}
            />
          ) : (
            <div
              className="slot-date-time-picker__slots-grid"
              role="listbox"
              aria-label={slotsAriaLabel}
            >
              {orphanSelection ? (
                <div className="slot-date-time-picker__orphan-slot">
                  <Button
                    type="primary"
                    className="slot-date-time-picker__slot-btn"
                    onClick={() => handleSlotSelect(orphanSelection)}
                  >
                    {orphanSelection.format(TIME_FORMAT)}
                  </Button>
                </div>
              ) : null}
              {slotStarts.map((slotStart) => (
                <Button
                  key={slotStart.toISOString()}
                  type={isSlotSelected(slotStart) ? 'primary' : 'default'}
                  className="slot-date-time-picker__slot-btn"
                  role="option"
                  aria-selected={isSlotSelected(slotStart)}
                  onClick={() => handleSlotSelect(slotStart)}
                >
                  {slotStart.format(TIME_FORMAT)}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (isInlineMode) {
    return <div className="slot-date-time-picker__inline">{pickerPanel}</div>;
  }

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      destroyOnHidden
      classNames={{ root: 'slot-date-time-picker__popover' }}
      getPopupContainer={getPopupContainer ?? (() => document.body)}
      content={pickerPanel}
    >
      <div style={{ width: '100%' }}>
        <Input
          key={`slot-datetime-trigger-${dialogKey}`}
          readOnly
          value={displayText}
          placeholder={placeholder}
          size={size}
          className={isLoadingSlots ? 'slot-date-time-picker__trigger--loading' : undefined}
          style={{ width: '100%', cursor: 'pointer' }}
          aria-busy={isLoadingSlots}
          suffix={
            isLoadingSlots ? (
              <Spin size="small" />
            ) : (
              <CalendarOutlined style={{ color: token.colorTextSecondary }} />
            )
          }
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open}
        />
      </div>
    </Popover>
  );
};

/**
 * Form field for choosing an appointment start time from resource availability slots.
 *
 * Renders a `Form.Item` bound to `startTime` and delegates to {@link SlotDateTimePickerControl}.
 * Use with `useAppointmentSlotSelection` in create and reschedule flows.
 *
 * @example
 * ```tsx
 * <SlotDateTimePicker
 *   dialogKey={dialogKey}
 *   disabledDate={selection.disabledDate}
 *   slotsByDateKey={selection.slotsByDateKey}
 *   timezone={selection.timezone}
 *   isLoadingSlots={selection.isLoadingVisibleMonthSlots}
 *   onCalendarDaySelect={selection.handleCalendarDaySelect}
 * />
 * ```
 */
export const SlotDateTimePicker: React.FC<SlotDateTimePickerProps> = ({
  resourcePolicy,
  duration: _duration,
  form: _form,
  dialogKey,
  disabledDate,
  disabledTime: _disabledTime,
  slotAlignmentMinutes: _slotAlignmentMinutes,
  useSlotSchedule: _useSlotSchedule = true,
  visibleMonth,
  onVisibleMonthChange,
  preferredPickerValue,
  onStartTimeCommit,
  onCalendarDaySelect,
  label: labelOverride,
  tooltip: tooltipOverride,
  placeholder: placeholderOverride,
  help: helpOverride,
  size = 'middle',
  getPopupContainer,
  locale = appointmentSlotDatePickerLocale,
  slotsByDateKey,
  timezone,
  isLoadingSlots,
  slotsTruncated = false,
  displayMode = 'popover'
}) => {
  const { token } = theme.useToken();
  const { t } = useTranslation('manage');

  const label = labelOverride ?? (
    <Space size={4}>
      <CalendarOutlined style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }} />
      <span>{t('appointmentComponents.create.dateTime.label')}</span>
    </Space>
  );
  const tooltip = tooltipOverride ?? (
    resourcePolicy
      ? t('appointmentComponents.create.dateTime.tooltipWithPolicy', {
          minLead: leadTimeMinutesToDays(resourcePolicy.minLeadTimeMinutes),
          maxLead: resourcePolicy.maxLeadTimeDays,
          alignment: resourcePolicy.slotAlignmentMinutes
        })
      : undefined
  );

  return (
    <Form.Item
      label={label}
      name="startTime"
      tooltip={tooltip}
      help={helpOverride}
      validateTrigger="onSubmit"
      rules={[{ required: true, message: t('appointmentComponents.create.dateTime.required') }]}
      preserve={false}
    >
      <SlotDateTimePickerControl
        key={`slot-datetime-${dialogKey}`}
        dialogKey={dialogKey}
        disabledDate={disabledDate}
        visibleMonth={visibleMonth}
        onVisibleMonthChange={onVisibleMonthChange}
        preferredPickerValue={preferredPickerValue}
        onStartTimeCommit={onStartTimeCommit}
        onCalendarDaySelect={onCalendarDaySelect}
        placeholder={placeholderOverride ?? t('appointmentComponents.create.dateTime.placeholder')}
        size={size}
        getPopupContainer={getPopupContainer}
        slotsByDateKey={slotsByDateKey}
        timezone={timezone}
        isLoadingSlots={isLoadingSlots}
        slotsTruncated={slotsTruncated}
        locale={locale}
        displayMode={displayMode}
      />
    </Form.Item>
  );
};
